import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, ApiError } from '@/lib/api-client';
import type { UserRole } from '@/lib/types';
import { useAuth } from '@/lib/auth-context';
import { useGroups } from '@/hooks/useGroups';
import { TableSkeleton } from '@/components/ui/spinner';
import { MultiSelect } from '@/components/ui/multi-select';

interface UserRow {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  groupIds: string[];
}

interface ApiPlatformError {
  detail?: string;
  title?: string;
  violations?: Array<{ propertyPath: string; message: string }>;
}

const roleBadge: Record<UserRole, { label: string; className: string }> = {
  admin: { label: 'Admin', className: 'bg-red-100 text-red-800' },
  operator: { label: 'Provozovatel', className: 'bg-blue-100 text-blue-800' },
  social_manager: { label: 'Social Manager', className: 'bg-purple-100 text-purple-800' },
};

export default function SettingsUsersPage() {
  const { user: currentUser, isAdmin } = useAuth();
  const { groups } = useGroups();
  const [users, setUsers] = useState<UserRow[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [formName, setFormName] = useState('');
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formRole, setFormRole] = useState<UserRole>('operator');
  const [formGroups, setFormGroups] = useState<string[]>([]);
  const [saveError, setSaveError] = useState('');
  const [deleteError, setDeleteError] = useState('');

  const loadUsers = async () => {
    setUsersLoading(true);
    try {
      const data = await apiClient.get<UserRow[]>('/users');
      setUsers(data);
    } catch {
      // silently ignore — admin guard prevents unauthorized access
    } finally {
      setUsersLoading(false);
    }
  };

  useEffect(() => {
    void loadUsers();
  }, []);

  if (!isAdmin) {
    return <div className="p-8 text-center text-muted-foreground">Nemáte přístup k administraci.</div>;
  }

  const openNew = () => {
    setEditId(null);
    setFormName('');
    setFormEmail('');
    setFormPassword('');
    setFormRole('operator');
    setFormGroups([]);
    setSaveError('');
    setDialogOpen(true);
  };

  const openEdit = (u: UserRow) => {
    setEditId(u.id);
    setFormName(u.name);
    setFormEmail(u.email);
    setFormPassword('');
    setFormRole(u.role);
    setFormGroups(u.groupIds);
    setSaveError('');
    setDialogOpen(true);
  };

  // Build user-friendly error message from API Platform RFC 7807 response
  const formatError = (e: unknown, fallback: string): string => {
    if (e instanceof ApiError) {
      const body = (e.body ?? {}) as ApiPlatformError;
      if (body.violations && body.violations.length > 0) {
        return body.violations.map((v) => `${v.propertyPath}: ${v.message}`).join(', ');
      }
      if (body.detail) return body.detail;
      if (e.message) return e.message;
    }
    return fallback;
  };

  const handleSave = async () => {
    setSaveError('');
    const body: Record<string, unknown> = {
      name: formName,
      email: formEmail,
      role: formRole,
      groupIds: formGroups,
    };
    if (formPassword) body.password = formPassword;

    try {
      if (editId) {
        await apiClient.patch<UserRow>(`/users/${editId}`, body);
        toast.success('Uživatel uložen');
      } else {
        await apiClient.post<UserRow>('/users', body);
        toast.success('Uživatel vytvořen');
      }
      await loadUsers();
      setDialogOpen(false);
    } catch (e) {
      setSaveError(formatError(e, 'Chyba při ukládání'));
    }
  };

  const handleDelete = async (id: string) => {
    if (id === currentUser?.id) return;
    if (!confirm('Opravdu smazat tohoto uživatele?')) return;
    setDeleteError('');
    try {
      await apiClient.delete<null>(`/users/${id}`);
      await loadUsers();
      toast.success('Uživatel smazán');
    } catch (e) {
      const msg = formatError(e, 'Chyba při mazání');
      setDeleteError(msg);
      toast.error(msg);
    }
  };


  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Uživatelé</h1>
          <p className="text-muted-foreground">Správa uživatelů a jejich přístupů</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger
              onClick={openNew}
              className="inline-flex items-center gap-2 rounded-md text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 h-9 px-4 cursor-pointer"
            >
              <Plus className="h-4 w-4" />
              Přidat uživatele
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editId ? 'Upravit uživatele' : 'Nový uživatel'}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Jméno</Label>
                  <Input value={formName} onChange={(e) => setFormName(e.target.value)} placeholder="Jan Novák" />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input type="email" value={formEmail} onChange={(e) => setFormEmail(e.target.value)} placeholder="jan@congusto.cz" />
                </div>
                <div className="space-y-2">
                  <Label>Heslo</Label>
                  <Input
                    type="password"
                    value={formPassword}
                    onChange={(e) => setFormPassword(e.target.value)}
                    placeholder={editId ? 'ponechte prázdné pro zachování' : 'zadejte heslo'}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={formRole} onValueChange={(v) => v && setFormRole(v as UserRole)}>
                    <SelectTrigger>
                      <SelectValue>
                        {(v: string) =>
                          ({ admin: 'Admin (vše)', operator: 'Provozovatel', social_manager: 'Social Manager' } as Record<string, string>)[v] ?? v
                        }
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin (vše)</SelectItem>
                      <SelectItem value="operator">Provozovatel</SelectItem>
                      <SelectItem value="social_manager">Social Manager</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Restaurace</Label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formGroups.includes('all')}
                      onChange={() => setFormGroups(formGroups.includes('all') ? [] : ['all'])}
                      className="rounded"
                    />
                    <span className="text-sm font-semibold">Všechny restaurace</span>
                  </label>
                  {!formGroups.includes('all') && (
                    <MultiSelect
                      options={groups.map((g) => ({
                        value: g.id,
                        label: g.name,
                        hint: [g.fb.length ? 'FB' : '', g.ig.length ? 'IG' : '', g.ga4.length ? 'GA4' : '']
                          .filter(Boolean)
                          .join(' · '),
                      }))}
                      value={formGroups}
                      onChange={setFormGroups}
                      placeholder="Vyber restaurace…"
                      emptyLabel="Žádné restaurace"
                    />
                  )}
                </div>
                {saveError && <p className="text-sm text-destructive">{saveError}</p>}
                <Button onClick={handleSave} className="w-full">
                  {editId ? 'Uložit změny' : 'Přidat uživatele'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Seznam uživatelů</CardTitle>
        </CardHeader>
        <CardContent>
          {deleteError && <p className="text-sm text-destructive mb-2">{deleteError}</p>}
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Jméno</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Restaurace</TableHead>
                <TableHead className="text-right">Akce</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {usersLoading && <TableSkeleton rows={4} cols={5} />}
              {!usersLoading && users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-sm text-muted-foreground py-8">
                    Žádní uživatelé
                  </TableCell>
                </TableRow>
              )}
              {!usersLoading && users.map((u) => (
                <TableRow key={u.id}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className={roleBadge[u.role].className}>
                      {roleBadge[u.role].label}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {u.groupIds.includes('all') ? (
                        <Badge variant="outline">Všechny</Badge>
                      ) : (
                        u.groupIds.map((gid) => {
                          const g = groups.find((gr) => gr.id === gid);
                          return (
                            <Badge key={gid} variant="outline" className="text-xs">
                              {g?.name || gid}
                            </Badge>
                          );
                        })
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(u)}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      {u.id !== currentUser?.id && (
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(u.id)}>
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
